import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useExperience } from '@/store/experience';

// Ice/dust particles in a column around the camera. Streak upward during fast descent.
export function Particles({ count }) {
  const ref = useRef();
  const frame = useExperience((s) => s.frame);
  const { positions, geometry } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) { pos[i * 3] = (Math.random() - 0.5) * 60; pos[i * 3 + 1] = (Math.random() - 0.5) * 80; pos[i * 3 + 2] = -Math.random() * 40 - 1; }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    return { positions: pos, geometry: geo };
  }, [count]);

  const material = useMemo(() => new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, depthTest: false,
    uniforms: { uStretch: { value: 1 }, uOpacity: { value: 0.6 } },
    vertexShader: `uniform float uStretch; varying float vA; void main(){ vec4 mv = modelViewMatrix * vec4(position,1.0); gl_Position = projectionMatrix * mv; float d = max(-mv.z, 0.01); gl_PointSize = clamp(90.0 / d, 1.0, 6.0) * (1.0 + uStretch * 0.4); vA = 1.0 - smoothstep(6.0, 45.0, d); }`,
    fragmentShader: `uniform float uOpacity; varying float vA; void main(){ float r = length(gl_PointCoord - 0.5); float a = (1.0 - smoothstep(0.1, 0.5, r)) * vA * uOpacity; gl_FragColor = vec4(1.0, 1.0, 1.0, a); }`,
  }), []);

  useFrame(({ camera }, dt) => {
    const cy = camera.position.y;
    const arr = geometry.attributes.position.array;
    const vel = frame.velocity;
    for (let i = 0; i < count; i++) {
      let y = arr[i * 3 + 1] + dt * 0.6;
      if (y > cy + 40) y -= 80; else if (y < cy - 40) y += 80;
      arr[i * 3 + 1] = y;
    }
    geometry.attributes.position.needsUpdate = true;
    material.uniforms.uStretch.value = Math.min(4, Math.abs(vel) * 0.1);
    material.uniforms.uOpacity.value = 0.35 + Math.min(0.5, Math.abs(vel) * 0.01);
    ref.current.position.x = camera.position.x;
  });

  return <points ref={ref} geometry={geometry} material={material} frustumCulled={false} renderOrder={2} />;
}
